import { TeaserStatus, CompanyType } from '../../../__generated__/graphql'
import { roundTo } from './calculations'

// Action handlers for teaser management
export const createActionHandlers = () => {
  const handleCreateTrafficDriver = async (record: any) => {
    console.log('Creating/updating teaser:', record)
    // In real implementation, this would call the mutation
    return true
  }

  const onUpdateStatus = (row: any, status: TeaserStatus) => {
    console.log('Updating teaser status:', row.trafficDriverId, status)
    // In real implementation, this would call the mutation
    // mockTeaserStatuses[row.trafficDriverId] = status
  }

  const onCreateOrUpdate = (row?: any) => {
    // This will be handled by the parent component
    return { teaser: row }
  }

  return {
    handleCreateTrafficDriver,
    onUpdateStatus,
    onCreateOrUpdate
  }
}
const toNumber = (value: any) => {
  if (value === null || value === undefined) {
    return 0
  }
  if (typeof value === 'number') {
    return value
  }
  const parsed = Number(value)
  return Number.isNaN(parsed) ? 0 : parsed
}

export interface TeaserTotals {
  tdImpressions: number
  tdClicks: number
  reads: number
  totalReadtime: number
  publisherRevenue: number
  totalCost: number
  clickOuts: number
  conversions: number
}

export interface ChannelTotals {
  reads: number
  totalReadtime: number
  clickOuts: number
  conversions: number
}

export const calculateTeaserTotals = (teasers: any[]): TeaserTotals => {
  return teasers.reduce<TeaserTotals>((acc, teaser) => {
    acc.tdImpressions += toNumber(teaser.tdImpressions)
    acc.tdClicks += toNumber(teaser.tdClicks)
    acc.reads += toNumber(teaser.reads)
    acc.totalReadtime += toNumber(teaser.total_readtime ?? teaser.totalReadtime)
    acc.publisherRevenue += toNumber(teaser.publisherRevenue)
    acc.totalCost += toNumber(teaser.totalCost)
    acc.clickOuts += toNumber(teaser.clickOuts)
    acc.conversions += toNumber(teaser.conversions)
    return acc
  }, {
    tdImpressions: 0,
    tdClicks: 0,
    reads: 0,
    totalReadtime: 0,
    publisherRevenue: 0,
    totalCost: 0,
    clickOuts: 0,
    conversions: 0
  })
}

export const calculateChannelTotals = (channels: any[]): ChannelTotals => {
  return channels.reduce<ChannelTotals>((acc, channel) => {
    acc.reads += toNumber(channel.reads)
    acc.totalReadtime += toNumber(channel.total_readtime ?? channel.totalReadtime)
    acc.clickOuts += toNumber(channel.clickOuts)
    acc.conversions += toNumber(channel.conversions)
    return acc
  }, {
    reads: 0,
    totalReadtime: 0,
    clickOuts: 0,
    conversions: 0
  })
}

export const createTeaserSummaryExportRow = (totals: TeaserTotals, companyType: CompanyType | string) => {
  const impressionsInThousands = totals.tdImpressions / 1000
  const baseSummary = {
    name: 'Total',
    tdImpressions: totals.tdImpressions,
    tdClicks: totals.tdClicks,
    reads: totals.reads,
    avgReadtime: totals.reads > 0 ? roundTo(totals.totalReadtime / totals.reads, 1) : 0,
    clickOuts: totals.clickOuts,
    conversions: totals.conversions,
    ctr: totals.tdImpressions > 0 ? roundTo((totals.tdClicks / totals.tdImpressions) * 100, 2) : 0,
    imps_reads: totals.tdImpressions > 0 ? roundTo((totals.reads / totals.tdImpressions) * 1000, 2) : 0,
    cotr: totals.reads > 0 ? roundTo((totals.clickOuts / totals.reads) * 100, 2) : 0
  }

  const isPublisher = companyType === CompanyType.Publisher || (typeof companyType === 'string' && companyType.toLowerCase() === 'publisher')

  if (isPublisher) {
    const publisherRevenue = roundTo(totals.publisherRevenue, 2)
    return {
      ...baseSummary,
      publisherRevenue,
      eCpm: impressionsInThousands > 0 ? roundTo(publisherRevenue / impressionsInThousands, 2) : 0
    }
  }

  const totalCost = roundTo(totals.totalCost, 2)
  return {
    ...baseSummary,
    totalCost,
    eCpm: impressionsInThousands > 0 ? roundTo(totalCost / impressionsInThousands, 2) : 0,
    cpa: totals.conversions > 0 ? roundTo(totalCost / totals.conversions, 2) : 0
  }
}

export const createChannelSummaryExportRow = (totals: ChannelTotals) => ({
  channel: 'Total',
  reads: totals.reads,
  avgReadtime: totals.reads > 0 ? roundTo(totals.totalReadtime / totals.reads, 1) : 0,
  clickOuts: totals.clickOuts,
  conversions: totals.conversions
})
